/**
 * PhotoSelectionBar Component
 * 
 * Bottom bar for multi-select operations.
 */

import React from "react";
import { View, Text, TouchableOpacity } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useThemeColors } from "@/providers/ThemeProvider";

interface PhotoSelectionBarProps {
  selectedCount: number;
  totalCount: number;
  onCancel: () => void;
  onSelectAll: () => void;
  onDownload: () => void;
}

/**
 * Selection action bar
 */
export const PhotoSelectionBar: React.FC<PhotoSelectionBarProps> = ({
  selectedCount,
  totalCount,
  onCancel,
  onSelectAll,
  onDownload,
}) => {
  const colors = useThemeColors();
  const insets = useSafeAreaInsets();

  const allSelected = selectedCount === totalCount && totalCount > 0;

  return (
    <View
      className="absolute bottom-0 left-0 right-0 flex-row items-center justify-between px-4"
      style={{
        backgroundColor: colors.surface,
        borderTopWidth: 1,
        borderTopColor: colors.border,
        paddingBottom: Math.max(insets.bottom, 16),
        paddingTop: 12,
      }}
    >
      {/* Cancel button */}
      <TouchableOpacity
        onPress={onCancel}
        className="flex-row items-center"
        accessibilityRole="button"
        accessibilityLabel="Selectie annuleren"
      >
        <Ionicons name="close" size={24} color={colors.textSecondary} />
        <Text className="ml-2" style={{ color: colors.textSecondary }}>
          Annuleren
        </Text>
      </TouchableOpacity>

      {/* Selected count */}
      <Text className="font-medium" style={{ color: colors.text }}>
        {selectedCount} geselecteerd
      </Text>

      {/* Actions */}
      <View className="flex-row items-center">
        {/* Select all */}
        <TouchableOpacity
          onPress={onSelectAll}
          className="mr-4"
          accessibilityRole="button"
          accessibilityLabel={allSelected ? "Alles deselecteren" : "Alles selecteren"}
        >
          <Ionicons
            name={allSelected ? "checkbox" : "checkbox-outline"}
            size={24}
            color={colors.primary}
          />
        </TouchableOpacity>

        {/* Download */}
        <TouchableOpacity
          onPress={onDownload}
          disabled={selectedCount === 0}
          className="flex-row items-center px-4 py-2 rounded-full"
          style={{
            backgroundColor: selectedCount > 0 ? colors.primary : colors.surfaceSecondary,
            opacity: selectedCount > 0 ? 1 : 0.5,
          }}
          accessibilityRole="button"
          accessibilityLabel={`${selectedCount} foto's downloaden`}
        >
          <Ionicons
            name="download"
            size={18}
            color={selectedCount > 0 ? "#FFFFFF" : colors.textTertiary}
          />
          <Text
            className="ml-1 font-medium"
            style={{ color: selectedCount > 0 ? "#FFFFFF" : colors.textTertiary }}
          >
            Download
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

export default PhotoSelectionBar;

